import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Link,
  styled,
  Typography,
} from '@mui/material';

export const ContainerStyled = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'isInModal',
})(({ theme, isInModal }) => ({
  height: isInModal ? 'calc(100vh - 56px)' : 'auto',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: isInModal
    ? theme.palette.colors.infoBackground
    : theme.palette.colors.newsBackground,
  padding: isInModal ? '16px 16px 0' : 0,
  border: 0,
  borderRadius: isInModal ? 0 : '12px',
  boxShadow: 'none',
  width: 'auto',

  [theme.breakpoints.up('md')]: {
    padding: isInModal && '16px 140px 0',
  },
  [theme.breakpoints.up('lg')]: {
    backgroundColor: theme.palette.colors.newsBackground,
    height: isInModal ? 'auto' : '365px',
    padding: isInModal ? '14px 0 24px' : '14px 20px 14px 14px',
    border: isInModal ? 'none' : `1px solid ${theme.palette.colors.borderLine}`,
    borderRadius: isInModal ? 0 : '5px',
    width: isInModal ? '420px' : 'auto',
  },
}));

export const ScrollableContainerStyled = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isInModal',
})(({ theme, isInModal }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',

  '@-moz-document url-prefix()': {
    scrollbarColor: `${theme.palette.colors.scrollOnDetailBackground} transparent`,
    scrollbarWidth: 'thin',
  },
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
    webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.colors.scrollOnDetailBackground,
    borderRadius: '5px',
  },

  [theme.breakpoints.up('lg')]: {
    flexDirection: isInModal ? 'column' : 'row',
    overflowY: 'auto',
  },
}));

export const MediaContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isShowingVideo',
})(({ theme, isShowingVideo }) => ({
  backgroundColor: isShowingVideo && '#000000',
  borderRadius: isShowingVideo && '12px',
  position: 'relative',

  '& .MuiChip-root': {
    position: 'absolute',
    bottom: 0,
    right: 0,
    margin: '0 8px 8px 0',
  },

  [theme.breakpoints.up('lg')]: {
    borderRadius: isShowingVideo && '5px',
  },
}));

export const ImageStyled = styled(CardMedia, {
  shouldForwardProp: (prop) => prop !== 'isInModal',
})(({ theme, isInModal }) => ({
  width: '100%',
  maxHeight: isInModal ? '219px' : '364px',
  borderRadius: isInModal ? '12px' : 0,
  objectPosition: isInModal ? 'top' : 'center',

  [theme.breakpoints.up('md')]: {
    maxHeight: isInModal && '280px',
  },
  [theme.breakpoints.up('lg')]: {
    width: !isInModal && 'auto',
    maxWidth: !isInModal && '420px',
    height: !isInModal && '100%',
    maxHeight: !isInModal && 'none',
    borderRadius: '5px',
  },
}));

export const VideoBorder = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'position',
})(({ theme, position }) => {
  const getBorderRadius = () => {
    if (position === 'top')
      return {
        borderTopLeftRadius: '12px',
        borderTopRightRadius: '12px',

        [theme.breakpoints.up('lg')]: {
          borderTopLeftRadius: '5px',
          borderTopRightRadius: '5px',
        },
      };

    return {
      borderBottomLeftRadius: '12px',
      borderBottomRightRadius: '12px',

      [theme.breakpoints.up('lg')]: {
        borderBottomLeftRadius: '5px',
        borderBottomRightRadius: '5px',
      },
    };
  };

  return {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#000000',
    height: '40px',
    ...getBorderRadius(),
  };
});

export const CloseVideoIconStyled = styled(FontAwesomeIcon, {
  shouldForwardProp: (prop) => prop !== 'hide',
})(({ hide }) => ({
  display: hide && 'none',
  color: '#ffffff',
  fontSize: '18px',
  alignSelf: 'self-end',
  margin: '8px 10px 0 0',
  cursor: 'pointer',
}));

export const ContentStyled = styled(CardContent, {
  shouldForwardProp: (prop) =>
    prop !== 'isInModal' && prop !== 'hasMedia' && prop !== 'hasFileToShow',
})(({ theme, isInModal, hasMedia, hasFileToShow }) => {
  const getPadding = () => {
    if (isInModal) {
      if (hasMedia) return '24px 0 0';

      return 0;
    }

    return '14px 14px 0';
  };

  const getBorderRadius = () => {
    if (!hasMedia) return { borderRadius: isInModal ? 0 : '12px' };

    return {
      borderBottomLeftRadius: isInModal ? 0 : '12px',
      borderBottomRightRadius: isInModal ? 0 : '12px',
    };
  };

  return {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: isInModal ? 'normal' : 'space-between',
    border: isInModal ? 0 : `1px solid ${theme.palette.colors.borderLine}`,
    ...getBorderRadius(),
    padding: isInModal ? '16px 0 0' : '16px',
    overflowY: isInModal && 'hidden',

    '&:last-child': {
      paddingBottom: isInModal && hasFileToShow ? 0 : '16px',
    },

    [theme.breakpoints.up('lg')]: {
      height: 'unset',
      border: 0,
      padding: getPadding(),
      overflowY: isInModal && 'unset',

      '&:last-child': {
        paddingBottom: 0,
      },
    },
  };
});

export const BottomSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isInModal',
})(({ theme, isInModal }) => ({
  height: !isInModal && '80%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  overflowY: isInModal && 'hidden',
  backgroundColor: isInModal && theme.palette.colors.infoBackground,

  '& > :last-child': {
    display: isInModal && 'none',
  },

  [theme.breakpoints.up('lg')]: {
    overflowY: isInModal && 'unset',
    backgroundColor: isInModal && 'transparent',
    '& > :last-child': {
      display: 'none',
    },
  },
}));

export const BodyStyled = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isInModal',
})(({ theme, isInModal }) => ({
  whiteSpace: 'pre-wrap',
  fontSize: '12px',
  lineHeight: 'normal',
  letterSpacing: 'normal',
  color: theme.palette.colors.onSecondaryAndDetailBackground,
  margin: isInModal ? '8px 0 0' : '16px 0',
  height: !isInModal && '90%',
  overflowY: isInModal ? 'auto' : 'hidden',
  display: !isInModal && '-webkit-box',
  WebkitLineClamp: !isInModal && 4,
  lineClamp: !isInModal && 4,
  WebkitBoxOrient: !isInModal && 'vertical',

  '@-moz-document url-prefix()': {
    scrollbarColor: `${theme.palette.colors.scrollOnDetailBackground} transparent`,
    scrollbarWidth: 'thin',
  },
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
    webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.colors.scrollOnDetailBackground,
    borderRadius: '5px',
  },

  [theme.breakpoints.up('lg')]: {
    overflowY: isInModal && 'hidden',
    margin: !isInModal && 0,
    marginBottom: !isInModal && '8px',
    display: 'block',
    WebkitLineClamp: 'unset',
    lineClamp: 'unset',
    WebkitBoxOrient: 'unset',
  },
}));

export const FileContainer = styled(Link, {
  shouldForwardProp: (prop) => prop !== 'isInModal',
})(({ theme }) => ({
  margin: '16px 0',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: 'normal',
  letterSpacing: 'normal',
  color: theme.palette.colors.onPrimaryBackground,

  ':hover': {
    textDecoration: 'underline',

    '& .fileLinesIcon': {
      color: theme.palette.colors.mainFocused,
    },
  },

  [theme.breakpoints.up('lg')]: {
    margin: '24px 0 0 0',
  },
}));

export const FileIconStyled = styled(FontAwesomeIcon, {
  shouldForwardProp: (prop) => prop !== 'isInModal',
})(({ theme }) => ({
  fontSize: '16px',
  marginRight: '8px',

  '&.fileLinesIcon': {
    color: theme.palette.colors.label,
  },
}));

export const DateStyled = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isInModal',
})(({ theme, isInModal }) => ({
  fontSize: '13px',
  lineHeight: 'normal',
  letterSpacing: 'normal',
  color: theme.palette.colors.onSecondaryAndDetailBackground,
  display: isInModal ? 'block' : 'none',

  [theme.breakpoints.up('lg')]: {
    display: 'block',
  },
}));
